import { createORPCClient as originalCreateClient } from "@orpc/client";
import { OpenAPILink } from "@orpc/openapi-client/fetch";
import { contract } from "@carry/contract";

export interface CreateCarryClientOptions {
  baseUrl: string;
  /** Authorization header に Bearer トークンを送る場合 (driver 等)。Better-Auth では未指定 (cookie 連携)。 */
  getAccessToken?: () => string | Promise<string>;
  headers?: Record<string, string>;
  /** クロスサイト cookie を送る場合は 'include' (Better-Auth 連携時の admin 側など)。 */
  credentials?: "omit" | "same-origin" | "include";
}

/**
 * carry-pilot API 用 oRPC クライアントリンク。
 * サーバーは {@link https://orpc.dev/docs/openapi OpenAPIHandler} なため、
 * RPC 専用の RPCLink ではなく contract の path（例: GET /api/v1/orders）に合う OpenAPILink を使う。
 */
export function createCarryLink(
  options: CreateCarryClientOptions,
): OpenAPILink<Record<string, never>> {
  const { baseUrl, getAccessToken, headers = {}, credentials } = options;
  const url = baseUrl.replace(/\/$/, "");
  const fetchFn: typeof fetch = credentials
    ? (input, init) => fetch(input, { ...init, credentials })
    : (input, init) => fetch(input, init);
  return new OpenAPILink(contract, {
    url,
    headers: async () => {
      const h: Record<string, string> = { ...headers };
      if (getAccessToken) {
        const token = await getAccessToken();
        if (token) h.Authorization = `Bearer ${token}`;
      }
      return h;
    },
    fetch: fetchFn,
  });
}

export { originalCreateClient as createORPCClient };
export { OpenAPILink } from "@orpc/openapi-client/fetch";
export type { JsonifiedClient } from "@orpc/openapi-client";

import type { JsonifiedClient } from "@orpc/openapi-client";
import type { ContractClient } from "@carry/contract";

/**
 * carry-pilot API の契約型付きクライアント (型の単一源泉)。
 * OpenAPILink 経由のレスポンスは JSON 化されるため JsonifiedClient でラップする。
 * createORPCClient の型引数に与えると全手続きが contract から型付けされる。
 */
export type CarryApiClient = JsonifiedClient<ContractClient>;
